import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { ReactComponent as DeleteIcon } from '../../../assets/icons/delete-icon.svg';
import { ReactComponent as PlusIcon } from '../../../assets/icons/plus.svg';
// import { AccountListInterFace } from '../../../interface/account-list-penalty-interface';
import { useAppDispatch } from '../../../redux/hooks';
import { setNotificationMessage } from '../../../redux/slices/NotificationManagementSlice';
import BatteryLoader from '../../../shared-components/battery-loader/BatteryLoader';
import MyButton from '../../../shared-components/button/Button';
import CustomDatePicker from '../../../shared-components/custom-date-range/CustomDatePicker';
import CustomInput from '../../../shared-components/custom-input/CustomInput';
import CustomSwitchButton from '../../../shared-components/custom-switch-button/custom-switch-button';
import ModalBox from '../../../shared-components/modal-box/ModalBox';
import MultiSelectDropdown from '../../../shared-components/multi-select-field/MultiSelectField';
// import PageTitle from '../../../shared-components/page-title/PageTitle';
import SelectFiled from '../../../shared-components/select-field/SelectField';
import SuccessNotification from '../../../shared-components/success-notification/SuccessNotification';
import ConvertDDMMYYYYtoISO from '../../../shared-functions/ConvertDDMMYYYYtoISO';
import ConvertUTCtoDate from '../../../shared-functions/ConvertUTCtoDate';
import { changeDateFormat } from '../../../shared-functions/DateTime';
import { stringValidation } from '../../../shared-functions/Validation';
import penaltyManagementApi from '../../../utils/api/penalty-management';

import AuditLog from './AuditLog';

interface OverrideInterface {
  accountCustomId: string;
  accountName: string;
  stationId: string[];
  ceilingType: string;
  ceilingValue: number;
  triggerMessage: string[];
  effectiveFrom: string;
  effectiveTo: string;
  dynamicPricing: string[];
  stationNames: StationInterface[];
  durationAmountList: DurationAmountListInterface[];
  status: boolean;
}

interface OverridePropertiesInterface {
  title: string;
  id?: string | null;
  type: 'edit' | 'add' | 'view';
  list?: 'account' | 'station';
  closeModal: () => void;
  handleRefresh?: () => void;
}

interface DurationAmountListInterface {
  from: string;
  to: string;
  mode: string;
  amount: string;
}

interface ModalBoxDetail {
  title: String;
  content: JSX.Element;
  notificationModal: boolean;
  rightCorner: string;
}

interface StationInterface {
  _id: string;
  name: string;
}

/**
 * Normalize a value to an array
 * @param value - string or string array
 * @returns - array of strings
 */
const normalizeToArray = (value?: string | string[]): string[] => {
  if (Array.isArray(value)) return value;
  if (value) return [value];
  return [];
};

/**
 * Edit Penalty
 * @returns
 */
function AddOverrides(properties: OverridePropertiesInterface) {
  const { t } = useTranslation('penaltyManagement');
  const { closeModal, id, type, handleRefresh, title, list } = properties;
  const dispatch = useAppDispatch();
  const [buttonStatus, setButtonStatus] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [ruleErrors, setRuleErrors] = useState<{
    [index: number]: { from: boolean; to: boolean };
  }>({});
  const [errorMessage, setErrorMessage] = useState<boolean>(false);
  const [modalBoxStatus, setModalBoxStatus] = useState<'open' | 'close'>('close');
  const [modalBoxDetail, setModalBoxDetail] = useState<ModalBoxDetail>({
    title: '',
    content: <div />,
    rightCorner: '',
    notificationModal: false,
  });
  const [accountErrorMessages, setAccountErrorMessages] = useState(false);
  const [override, setOverride] = useState<OverrideInterface>({
    accountCustomId: '',
    accountName: '',
    stationId: [],
    ceilingType: '',
    ceilingValue: 0,
    triggerMessage: [],
    effectiveFrom: '',
    effectiveTo: '',
    dynamicPricing: [],
    stationNames: [],
    durationAmountList: [{ from: '', to: '', mode: '', amount: '' }],
    status: false,
  });
  const [getAccountId, setGetAccountId] = useState('');

  /**
   * Normal Input Handler
   */
  const handleInputChange = (name: string, value: any) => {
    setAccountErrorMessages(false);
    setErrorMessage(false);
    setOverride((previous) => ({
      ...previous,
      [name]: value,
    }));
  };

  const allModes = [
    { label: t('grace'), value: 'grace' },
    { label: t('flatRate'), value: 'flatRate' },
    { label: t('perMinute'), value: 'perMinute' },
  ];

  /**
   * Add rule row
   */
  const addRule = () => {
    const index = override.durationAmountList.length - 1;
    const lastRule = override.durationAmountList[index];
    const from = Number(lastRule.from);
    const to = Number(lastRule.to);
    let fromError = false;
    let toError = false;

    // Case 1: Empty values
    if (lastRule.from === '') {
      fromError = true;
    }

    if (lastRule.to === '') {
      toError = true;
    }
    // Case 2: Empty or invalid TO
    if (lastRule.to === '' || to <= from) {
      toError = true;
    }

    // Case 2: Range invalid
    if (lastRule.from !== '' && lastRule.to !== '' && to < from) {
      fromError = true;
      toError = true;
    }

    // If any error → show error only for this row
    if (fromError || toError) {
      setRuleErrors((previous) => ({
        ...previous,
        [index]: { from: fromError, to: toError },
      }));
      return;
    }

    // Clear error if valid
    setRuleErrors((previous) => ({
      ...previous,
      [index]: { from: false, to: false },
    }));

    // If valid → create new rule
    setOverride((previous) => ({
      ...previous,
      durationAmountList: [
        ...previous.durationAmountList,
        {
          from: String(to), // next rule starts from previous TO
          to: '',
          mode: '',
          amount: '',
        },
      ],
    }));
  };

  /**
   * Delete rule row
   */
  const deleteRule = (index: number) => {
    setOverride((previous) => ({
      ...previous,
      durationAmountList: previous.durationAmountList.filter((_, input) => input !== index),
    }));
  };

  /**
   * Update rule row field value
   */
  const updateRule = (index: number, name: keyof DurationAmountListInterface, value: string) => {
    const durationAmount = [...override.durationAmountList];

    // Update field
    durationAmount[index][name] = value;

    // ✅ If mode is GRACE → force amount = "0"
    if (name === 'mode' && value === 'grace') {
      durationAmount[index].amount = '0';
    }

    // ✅ If switching away from grace → clear amount
    if (name === 'mode' && value !== 'grace') {
      durationAmount[index].amount = '';
    }

    setOverride((previous) => ({
      ...previous,
      durationAmount,
    }));
  };

  /**
   * Filter available mode options based on already selected modes
   */
  const getAvailableModes = (currentIndex: number) => {
    const selectedModes = override.durationAmountList.map((r) => r.mode).filter(Boolean);

    return allModes.filter((m) => {
      // Allow any mode if it's already selected for the current index (editing case)
      if (override.durationAmountList[currentIndex].mode === m.value) return true;
      // Make "Grace" unique: if already selected anywhere else, hide it
      if (m.value === 'grace' && selectedModes.includes('grace')) {
        return false;
      }

      // Allow Flat Rate & Per Minute multiple times
      return true;
    });
  };

  /**
   * Close modal function
   */
  const handleClose = () => {
    closeModal(); // close parent modal
    handleRefresh?.(); // trigger refresh
  };

  /**
   * Show success notification
   * @param title - Notification title
   */
  const showSuccess = (notificationTitle: string) => {
    setModalBoxStatus('open');
    setModalBoxDetail({
      title: '',
      rightCorner: '',
      content: (
        <SuccessNotification
          title={notificationTitle}
          description=""
          notificationCloseTimer={3000}
          onNotificationClose={handleClose} // ✅ close + refresh here
        />
      ),
      notificationModal: true,
    });
  };

  /**
   * Rules Valid
   */
  const isRulesValid = (): boolean => {
    let valid = true;
    const errors: {
      [index: number]: { from: boolean; to: boolean };
    } = {};

    override.durationAmountList.forEach((rule, index) => {
      const from = Number(rule.from);
      const to = Number(rule.to);

      let fromError = false;
      let toError = false;

      // 1️⃣ Empty checks
      if (rule.from === '') fromError = true;
      if (rule.to === '') toError = true;

      // 2️⃣ Same value (10–10)
      if (rule.from !== '' && rule.to !== '' && from === to) {
        toError = true;
      }

      // 3️⃣ to < from
      if (rule.from !== '' && rule.to !== '' && to < from) {
        fromError = true;
        toError = true;
      }

      if (index > 0) {
        const previousTo = Number(override.durationAmountList[index - 1].to);

        if (rule.from !== '' && !Number.isNaN(previousTo) && from !== previousTo) {
          fromError = true; // ❌ breaks chain
        }
      }

      if (fromError || toError) {
        valid = false;
        errors[index] = { from: fromError, to: toError };
      }

      // 5️⃣ Mode validation
      if (!rule.mode) valid = false;

      // 6️⃣ Amount validation
      if (rule.mode !== 'grace' && rule.amount === '') valid = false;
    });

    setRuleErrors(errors);
    return valid;
  };
  const isDateInvalid =
    override.effectiveFrom &&
    override.effectiveTo &&
    new Date(ConvertDDMMYYYYtoISO(override.effectiveFrom)) >
      new Date(ConvertDDMMYYYYtoISO(override.effectiveTo));

  /**
   * Submit payload to API
   */
  const handleSubmit = async () => {
    setButtonStatus(true);
    const isFormInvalid =
      override.ceilingType === '' ||
      !stringValidation(1, 100, override.ceilingType) ||
      override.ceilingValue === 0 ||
      override.dynamicPricing.length === 0 ||
      override.triggerMessage.length === 0 ||
      override.effectiveFrom === '' ||
      override.effectiveTo === '' ||
      new Date(ConvertDDMMYYYYtoISO(override.effectiveFrom)) >
        new Date(ConvertDDMMYYYYtoISO(override.effectiveTo)) ||
      !isRulesValid();

    if (isFormInvalid) {
      setErrorMessage(true);
      setButtonStatus(false);
      return;
    }
    const payloadStation = {
      stationId: override.stationId,
    };
    const payload = {
      accountCustomId: override.accountCustomId,
      dynamicPricing: override.dynamicPricing,
      ceilingType: override.ceilingType,
      ceilingValue: override.ceilingValue,
      triggerMessage: override.triggerMessage,
      effectiveFrom: ConvertDDMMYYYYtoISO(override.effectiveFrom),
      effectiveTo: ConvertDDMMYYYYtoISO(override.effectiveTo),
      durationAmountList: override.durationAmountList,
    };

    try {
      if (type === 'add' && list === 'account') {
        if (
          new Date(ConvertDDMMYYYYtoISO(override.effectiveFrom)) >
          new Date(ConvertDDMMYYYYtoISO(override.effectiveTo))
        ) {
          setButtonStatus(false);
          dispatch(
            setNotificationMessage({
              type: 'error',
              notificationDetails: {
                statusCode: 404,
                message: 'Effective From Date is Invalid',
              },
            }),
          );
        } else {
          await penaltyManagementApi.addAccountOverride(payload);
          showSuccess(t('overrideAddedSuccess'));
        }
      } else if (type === 'add' && list !== 'account') {
        if (
          new Date(ConvertDDMMYYYYtoISO(override.effectiveFrom)) >
          new Date(ConvertDDMMYYYYtoISO(override.effectiveTo))
        ) {
          setButtonStatus(false);
          dispatch(
            setNotificationMessage({
              type: 'error',
              notificationDetails: {
                statusCode: 404,
                message: 'Effective From Date is Invalid',
              },
            }),
          );
        } else {
          await penaltyManagementApi.addStationOverride({
            ...payload,
            stationId: payloadStation.stationId,
          });
        }
        showSuccess(t('overrideAddedSuccess'));
      } else if (type === 'edit' && list === 'account') {
        await penaltyManagementApi.updateSingleAccountOverrideDetails(id as string, {
          ...payload,
        });
        showSuccess(t('overrideUpdatedSuccess'));
      } else {
        await penaltyManagementApi.updateSingleStationOverrideDetails(id as string, {
          ...payload,
          stationId: payloadStation.stationId.toString(),
        });
        showSuccess(t('overrideUpdatedSuccess'));
      }
    } catch (error: any) {
      dispatch(
        setNotificationMessage({
          message: error?.response?.data?.message || t('somethingWentWrong'),
          status: error?.response?.status || 500,
          type: 'error',
          code: error?.response?.data?.code,
        }),
      );
    } finally {
      setButtonStatus(false);
    }
  };

  /**
   * station list Api
   */
  const handleStationList = async () => {
    try {
      penaltyManagementApi.findStationNames(override.accountCustomId).then((response) => {
        setOverride((previous) => ({
          ...previous,
          stationNames: response.data || [],
        }));
      });
      setOverride((previous) => ({
        ...previous,
        accountName: '',
      }));
    } catch (error: any) {
      dispatch(
        setNotificationMessage({
          message: error?.response?.data?.message || t('somethingWentWrong'),
          status: true,
          type: 'error',
          code: error?.response?.data?.code,
        }),
      );
    }
  };
  /**
   * Fetching account name list
   */
  const fetchAccountName = async () => {
    try {
      const response = await penaltyManagementApi.fetchAccountNameById(override.accountCustomId);
      if (!response?.data) return;
      setOverride((previous) => ({
        ...previous,
        accountName: response?.data?.accountName || 'N/A',
      }));
      setGetAccountId(override.accountCustomId);
    } catch (error: any) {
      dispatch(
        setNotificationMessage({
          message: error?.response?.data?.message || t('somethingWentWrong'),
          status: true,
          type: 'error',
          code: error?.response?.data?.code,
        }),
        setAccountErrorMessages(true),
      );
    }
  };

  let accountErrorText = '';

  if (override.accountCustomId === '') {
    accountErrorText = 'required';
  } else if (override.accountCustomId.length !== 20) {
    accountErrorText = 'accountIdMustBe20Chars';
  } else if (accountErrorMessages) {
    accountErrorText = 'facilityAccountNotFound';
  }
  useEffect(() => {
    if (!id) return;

    /**
     *fetchOverrideDetails
     */
    // eslint-disable-next-line @typescript-eslint/no-shadow
    const fetchOverrideDetails = async (id: any) => {
      try {
        setIsLoading(true);

        let response;

        if (list === 'account') {
          response = await penaltyManagementApi.viewSingleAccountOverrideDetails(id);
        } else {
          response = await penaltyManagementApi.viewSingleStationOverrideDetails(id);
        }

        const data = response?.data || {};

        setOverride({
          accountCustomId: data.accountCustomId || '',
          accountName: data.accountName || '',
          stationId: normalizeToArray(data.stationId),
          ceilingType: data.ceilingType || '',
          ceilingValue: data.ceilingValue || 0,
          triggerMessage: normalizeToArray(data.triggerMessage),
          dynamicPricing: normalizeToArray(data.dynamicPricing),
          stationNames: Array.isArray(data.stationNames) ? data.stationNames : [],
          effectiveFrom: ConvertUTCtoDate(data.effectiveFrom) || '',
          effectiveTo: ConvertUTCtoDate(data.effectiveTo) || '',
          durationAmountList:
            Array.isArray(data.durationAmountList) && data.durationAmountList.length
              ? data.durationAmountList.map((item: any) => ({
                  from: String(item.from),
                  to: String(item.to),
                  mode: item.mode,
                  amount: String(item.amount),
                }))
              : [{ from: '', to: '', mode: '', amount: '' }],
          status: Boolean(data.facilityRequest),
        });
      } catch (error: any) {
        dispatch(
          setNotificationMessage({
            message: error?.response?.data?.message || t('somethingWentWrong'),
            status: error?.response?.status || 500,
            type: 'error',
            code: error?.response?.data?.code,
          }),
        );
      } finally {
        setIsLoading(false);
      }
    };

    fetchOverrideDetails(id);
  }, [id, list]);

  useEffect(() => {
    if (!override.accountCustomId || override.accountCustomId.length !== 20) return;
    handleStationList();
  }, [override.accountCustomId]);

  useEffect(() => {
    if (override.accountCustomId.length >= 20) {
      fetchAccountName();
    }
  }, [override.accountCustomId]);

  const [disabledEditValues, setDisabledEditValues] = useState(false);

  useEffect(() => {
    /**
     * Fetch global setting quick view data
     */
    const loadData = async () => {
      if (!getAccountId) return;
      if (list === 'account' ||type === 'view' || type === 'edit') return;
      setIsLoading(true);

      try {
        const response = await penaltyManagementApi.fetchAccountSetting(getAccountId);
        const data = response?.data || {};
        const triggerMessage = normalizeToArray(data.triggerMessage);
        const dynamicPricing = normalizeToArray(data.dynamicPricing);

        //  condition → if values exist
        if (data.accountCustomId && (triggerMessage.length > 0 || dynamicPricing.length > 0)) {
          setDisabledEditValues(true);
        } else {
          setDisabledEditValues(false);
        }

        if (data.accountCustomId) {
          setOverride((previous) => ({
            ...previous,
            ceilingType: data.ceilingType || '',
            ceilingValue: data.ceilingValue || 0,

            triggerMessage: normalizeToArray(data.triggerMessage),
            dynamicPricing: normalizeToArray(data.dynamicPricing),

            effectiveFrom: ConvertUTCtoDate(data.effectiveFrom) || '',
            effectiveTo: ConvertUTCtoDate(data.effectiveTo) || '',

            durationAmountList:
              Array.isArray(data.durationAmountList) && data.durationAmountList.length
                ? data.durationAmountList.map((item: any) => ({
                    from: String(item.from),
                    to: String(item.to),
                    mode: item.mode,
                    amount: String(item.amount),
                  }))
                : [{ from: '', to: '', mode: '', amount: '' }],

            status: Boolean(data.facilityRequest),
          }));
        }
      } catch (error: any) {
        dispatch(
          setNotificationMessage({
            message: error?.response?.data?.message || t('somethingWentWrong'),
            status: error?.response?.status || 500,
            type: 'error',
            code: error?.response?.data?.code,
          }),
        );
      } finally {
        setIsLoading(false);
      }
    };

    loadData();
  }, [getAccountId]);

  return (
    <div className={`override-wrap ${type === 'add' ? 'add' : ''}`}>
      {isLoading ? (
        <div className="loader">
          <BatteryLoader />
        </div>
      ) : (
        <>
          <ModalBox
            open={modalBoxStatus === 'open'}
            title={modalBoxDetail.title}
            content={modalBoxDetail.content}
            closeModal={() => setModalBoxStatus('close')}
            rightCorner={modalBoxDetail.rightCorner}
            notification={modalBoxDetail.notificationModal}
            header={!modalBoxDetail.notificationModal}
          />
          <p className="page-title">{title}</p>
          <div className={`override-container ${type === 'edit' || type === 'view' ? 'edit' : ''}`}>
            <div className="override-left">
              {type === 'edit' && (
                <div className="switch-button">
                  <CustomSwitchButton
                    checked={override.status}
                    change={(checked) => handleInputChange('status', checked)}
                  />
                  <span>{t('penaltyFeature')}</span>
                </div>
              )}
              <div className="inputs-container">
                <div className="input-side-by-side">
                  <CustomInput
                    name="accountCustomId"
                    inputLabel={t('accountId')}
                    type="text"
                    placeholder={t('enterValue')}
                    value={override.accountCustomId}
                    onChangeValue={(name, value) => {
                      const stringValue = String(value);
                      handleInputChange(name, stringValue.slice(0, 20));
                    }}
                    error={
                      (errorMessage && override.accountCustomId === '') ||
                      accountErrorMessages ||
                      override.accountCustomId.length !== 20
                    }
                    errorMessage={t(accountErrorText)}
                    disabled={['edit', 'view'].includes(type)}
                    valueRequired
                  />
                  <CustomInput
                    name="accountName"
                    inputLabel={t('accountName')}
                    type="text"
                    placeholder={t('enterValue')}
                    value={override.accountName}
                    onChangeValue={handleInputChange}
                    disabled
                  />
                  {list !== 'account' && (
                    <MultiSelectDropdown
                      name="stationId"
                      title={t('stationId')}
                      selectedValues={override.stationId}
                      options={override?.stationNames?.map((station) => ({
                        label: station.name,
                        value: station._id,
                      }))}
                      valueRequired
                      onSelectionChange={handleInputChange}
                      placeholder={t('selectStationPlaceholder')}
                      placeholderFilled={t('stations')}
                      error={errorMessage && override.stationId.length === 0}
                      errorMessage={t('required')}
                      disabled={['edit', 'view'].includes(type)}
                    />
                  )}

                  <MultiSelectDropdown
                    name="dynamicPricing"
                    title={t('dynamicPricing')}
                    selectedValues={override.dynamicPricing}
                    onSelectionChange={handleInputChange}
                    placeholder={t('selectOption')}
                    placeholderFilled={t('options')}
                    options={[
                      { label: t('base'), value: 'base' },
                      { label: t('peak'), value: 'peak' },
                      { label: t('offPeak'), value: 'offpeak' },
                    ]}
                    search={false}
                    multiSelectDisable={false}
                    valueRequired
                    fontSize="small"
                    error={errorMessage && override.dynamicPricing.length === 0}
                    errorMessage={t('required')}
                    disabled={['view'].includes(type) || disabledEditValues}
                    selectedChips
                  />
                  <SelectFiled
                    name="ceilingType"
                    inputLabel={t('ceilingType')}
                    selectedValue={override.ceilingType}
                    placeholder={t('selectCeilingType')}
                    onChangeValue={handleInputChange}
                    valueRequired
                    error={errorMessage && override.ceilingType === ''}
                    errorMessage={t('required')}
                    options={[
                      { label: t('fixedAmount'), value: 'fixedAmount' },
                      { label: t('percentage'), value: 'percentage' },
                    ]}
                    disabled={['view'].includes(type)}
                  />
                  <CustomInput
                    name="ceilingValue"
                    inputLabel={t('maxCeilingValue')}
                    type="number"
                    placeholder={t('enterValue')}
                    value={override.ceilingValue}
                    onChangeValue={handleInputChange}
                    error={errorMessage && override.ceilingValue <= 0}
                    errorMessage={t('required')}
                    valueRequired
                    disabled={['view'].includes(type)}
                  />
                </div>
                <MultiSelectDropdown
                  name="triggerMessage"
                  title={t('triggerMessage')}
                  selectedValues={override.triggerMessage}
                  onSelectionChange={handleInputChange}
                  placeholder={t('selectOption')}
                  placeholderFilled={t('options')}
                  options={[
                    { label: 'SuspendedEVSE', value: 'SuspendedEVSE' },
                    { label: 'SuspendedEV', value: 'SuspendedEV' },
                    { label: 'Faulted', value: 'Faulted' },
                    { label: 'Finishing', value: 'Finishing' },
                  ]}
                  search={false}
                  multiSelectDisable={false}
                  valueRequired
                  fontSize="small"
                  selectedChips
                  error={errorMessage && override.triggerMessage.length === 0}
                  errorMessage={t('required')}
                  disabled={['view'].includes(type) || disabledEditValues}
                />
                <div className="date-box-content">
                  <CustomDatePicker
                    inputLabel={t('startDate')}
                    value={override.effectiveFrom}
                    name="effectiveFrom"
                    onChangeValue={handleInputChange}
                    placeholder={t('inputPlaceholder')}
                    error={Boolean(
                      errorMessage && (override.effectiveFrom === '' || isDateInvalid),
                    )}
                    errorMessage={
                      override.effectiveFrom === ''
                        ? t('startDateRequired')
                        : t('startDateGreaterError')
                    }
                    readOnly={['view'].includes(type)}
                    valueRequired
                    minDate={changeDateFormat(new Date().toString())}
                  />
                  <CustomDatePicker
                    inputLabel={t('endDate')}
                    value={override.effectiveTo}
                    name="effectiveTo"
                    onChangeValue={handleInputChange}
                    placeholder={t('inputPlaceholder')}
                    error={Boolean(errorMessage && (override.effectiveTo === '' || isDateInvalid))}
                    errorMessage={
                      override.effectiveTo === '' ? t('endDateRequired') : t('endDateLessError')
                    }
                    readOnly={['view'].includes(type)}
                    valueRequired
                    minDate={changeDateFormat(new Date().toString())}
                  />
                </div>
              </div>
              <div className="pricing-wrap">
                {override.durationAmountList.map((rule, index) => (
                  <div className="rule-box-container">
                    <div className="rule-box" key={index}>
                      <CustomInput
                        name="from"
                        inputLabel={t('fromMins')}
                        placeholder={t('enterFromMins')}
                        type="number"
                        value={rule.from}
                        onChangeValue={(name, value) =>
                          updateRule(
                            index,
                            name as keyof DurationAmountListInterface,
                            String(value),
                          )
                        }
                        error={ruleErrors[index]?.from || (errorMessage && rule.from === '')}
                        errorMessage={t('required')}
                        disabled={['view'].includes(type)}
                      />{' '}
                      <CustomInput
                        name="to"
                        inputLabel={t('toMins')}
                        placeholder={t('enterToMins')}
                        type="number"
                        value={rule.to}
                        onChangeValue={(name, value) =>
                          updateRule(
                            index,
                            name as keyof DurationAmountListInterface,
                            String(value),
                          )
                        }
                        error={ruleErrors[index]?.to || (errorMessage && rule.to === '')}
                        errorMessage={t('required')}
                        disabled={['view'].includes(type)}
                      />
                      <SelectFiled
                        name="mode"
                        inputLabel={t('mode')}
                        placeholder={t('enterMode')}
                        selectedValue={rule.mode}
                        onChangeValue={(name, value) =>
                          updateRule(
                            index,
                            name as keyof DurationAmountListInterface,
                            String(value),
                          )
                        }
                        options={getAvailableModes(index)}
                        error={rule.mode === '' && errorMessage}
                        errorMessage={t('required')}
                        disabled={['view'].includes(type)}
                      />
                      <CustomInput
                        name="amount"
                        inputLabel={t('amount')}
                        placeholder={t('enterValue')}
                        type="number"
                        value={rule.amount}
                        onChangeValue={(name, value) =>
                          updateRule(
                            index,
                            name as keyof DurationAmountListInterface,
                            String(value),
                          )
                        }
                        error={rule.amount === '' && errorMessage}
                        errorMessage={t('required')}
                        disabled={['view'].includes(type) || rule.mode === 'grace'}
                      />
                      <div className="delete-button">
                        {override.durationAmountList.length > 1 && (
                          <MyButton
                            label=""
                            svgIcon={<DeleteIcon />}
                            buttonType="error"
                            onClickFunc={() => deleteRule(index)}
                            disabled={buttonStatus || ['view'].includes(type)}
                          />
                        )}
                      </div>
                    </div>
                    {type !== 'view' && (
                      <div className="add-button">
                        {index === override.durationAmountList.length - 1 && (
                          <MyButton
                            label={t('add')}
                            svgIcon={<PlusIcon />}
                            buttonType="primary-outline-none"
                            onClickFunc={addRule}
                            disabled={buttonStatus || ['view'].includes(type)}
                          />
                        )}
                      </div>
                    )}
                  </div>
                ))}
              </div>
              {type !== 'view' && (
                <div className="button-row">
                  <MyButton
                    label={t('cancel')}
                    buttonType="primary-outline"
                    onClickFunc={closeModal}
                    disabled={buttonStatus || ['view'].includes(type)}
                  />
                  <MyButton
                    label={t('submit')}
                    buttonType="submit"
                    onClickFunc={handleSubmit}
                    loading={buttonStatus}
                    disabled={['view'].includes(type)}
                  />
                </div>
              )}
            </div>
            <div className="AuditLog">
              {id && id === 'okay' && (
                <AuditLog
                  currentData={{
                    id,
                  }}
                />
              )}
            </div>
          </div>
        </>
      )}
    </div>
  );
}

AddOverrides.defaultProps = {
  id: null,
  list: null,
};
export default AddOverrides;
